import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { SharePointPreviewResponse as SharePointPreview } from '../../types';

interface SharePointFile {
  id: string;
  name?: string;
  path?: string;
  fileType?: string;
  columns?: Array<string | { name?: string; columnName?: string; dataType?: string; sampleValues?: string[] }>;
}

export interface EmployeeIdMapping {
  fileId: string;
  fileName: string;
  userIdColumn?: string;
  personIdExternalColumn?: string;
  sampleValues: string[];
}

export interface EmployeeMatchResult {
  totalEmployees: number;
  matchingEmployees: number;
  unmatchedEmployees: Array<{ fileId: string; values: string[]; reason?: string }>;
}

interface EmployeeIdMappingStepProps {
  customerId: string;
  phaseId: string;
  sharePointSelection?: SharePointFile[];
  existingMappings?: EmployeeIdMapping[];
  existingMatchResult?: EmployeeMatchResult | null;
  onComplete: (mappings: EmployeeIdMapping[], result: EmployeeMatchResult | null) => void;
}

const EmployeeIdMappingStep: React.FC<EmployeeIdMappingStepProps> = ({
  customerId,
  phaseId,
  sharePointSelection = [],
  existingMappings = [],
  existingMatchResult,
  onComplete
}) => {
  const queryClient = useQueryClient();

  const { data: selectionData } = useQuery<SharePointFile[]>({
    queryKey: ['sharepointSelection', customerId, phaseId],
    queryFn: () => apiClient.getSharePointSelection(customerId, phaseId),
    enabled: !!(customerId && phaseId)
  });

  const selections: SharePointFile[] = selectionData ?? sharePointSelection ?? [];

  const [mappings, setMappings] = useState<EmployeeIdMapping[]>(Array.isArray(existingMappings) ? existingMappings : []);
  const [matchResult, setMatchResult] = useState<EmployeeMatchResult | null>(existingMatchResult || null);
  const [previewCache, setPreviewCache] = useState<Record<string, SharePointPreview | null>>({});

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [activePreviewFileId, setActivePreviewFileId] = useState<string | null>(null);

  const previewMutation = useMutation({
    mutationFn: (selectionId: string) => apiClient.getSharePointPreview(customerId, phaseId, selectionId),
    onSuccess: (data) => {
      if (data?.fileId) {
        setPreviewCache(prev => ({
          ...prev,
          [data.fileId]: data
        }));
        setActivePreviewFileId(data.fileId);
        setPreviewModalOpen(true);
      }
    }
  });

  useEffect(() => {
    setMatchResult(existingMatchResult || null);
  }, [existingMatchResult]);

  useEffect(() => {
    setMappings(prev => {
      // Ensure prev is an array
      const currentMappings = Array.isArray(prev) ? prev : [];
      // Index current mappings by fileId
      const prevMap = new Map(currentMappings.map(m => [m.fileId, m]));

      // Merge selections with existing local state
      return selections.map(file => {
        // 1. Preserve local state if it exists
        if (prevMap.has(file.id)) {
          return prevMap.get(file.id)!;
        }

        // 2. Fallback to prop-provided existing mapping
        const validExisting = Array.isArray(existingMappings) ? existingMappings : [];
        const propMapping = validExisting.find(m => m.fileId === file.id);
        if (propMapping) return propMapping;

        // 3. Create new valid mapping entry
        return {
          fileId: file.id,
          fileName: file.name || file.path || file.id,
          userIdColumn: undefined,
          personIdExternalColumn: undefined,
          sampleValues: []
        };
      });
    });
  }, [selections, existingMappings]);

  const filesWithColumns = useMemo(() => {
    return selections.map((file: SharePointFile) => {
      const columns = (file.columns || []).map(col => {
        if (typeof col === 'string') {
          return {
            key: col,
            label: col,
            dataType: 'string',
            sampleValues: [] as string[]
          };
        }
        return {
          key: col?.name || col?.columnName || 'column',
          label: col?.name || col?.columnName || 'column',
          dataType: col?.dataType || 'string',
          sampleValues: col?.sampleValues || []
        };
      });
      return {
        id: file.id,
        displayName: file.name || file.path || file.id,
        columns
      };
    });
  }, [selections]);

  const saveEmployeeIdMapping = useMutation({
    mutationFn: async (payload: EmployeeIdMapping[]) => {
      console.log('saveEmployeeIdMapping mutation called with payload:', payload);



      // Filter out mappings without any identifier columns
      const validMappings = payload.filter(mapping => mapping.userIdColumn || mapping.personIdExternalColumn);

      console.log('Valid mappings to send:', validMappings);

      if (validMappings.length === 0) {
        throw new Error('At least one file must have an identifier column mapped.');
      }

      const previews = await Promise.all(validMappings.map(async (mapping) => {
        const cached = previewCache[mapping.fileId];
        if (cached) return cached;
        try {
          const preview = await apiClient.getSharePointPreview(customerId, phaseId, mapping.fileId);
          return preview;
        } catch (error) {
          console.warn('Failed to load preview for selection', mapping.fileId, error);
          return null;
        }
      }));

      previews.forEach(preview => {
        if (!preview) return;
        setPreviewCache(prev => ({
          ...prev,
          [preview.fileId]: preview
        }));
      });

      return apiClient.saveEmployeeIdMappings(customerId, phaseId, validMappings);
    },
    onSuccess: (data) => {
      console.log('saveEmployeeIdMapping success:', data);
      queryClient.invalidateQueries({ queryKey: ['employeeIdMappings', customerId, phaseId] });
      queryClient.invalidateQueries({ queryKey: ['files', customerId, phaseId] });
      queryClient.invalidateQueries({ queryKey: ['sharepointSelection', customerId, phaseId] });

      const result = data?.matchResult || null;
      console.log('Setting match result:', result);
      setMatchResult(result);

      toast.success('Employee ID mapping validated successfully');

      // Don't automatically complete if this is a re-validation
      if (!matchResult) {
        onComplete(mappings, result);
      }
    },
    onError: (error: any) => {
      console.error('saveEmployeeIdMapping error:', error);
      console.error('Error details:', {
        response: error?.response,
        message: error?.message,
        stack: error?.stack
      });
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to validate employee ID mapping');
    }
  });

  const updateMapping = (fileId: string, columnName: string, identifierType: 'userId' | 'personIdExternal') => {
    const preview = previewCache[fileId];
    const sampleValues = preview?.rows
      ?.map(row => row[columnName])
      .filter((value: any) => value !== undefined && value !== null)
      .slice(0, 5) ?? [];

    setMappings(prev => {
      const existing = prev.find(m => m.fileId === fileId);
      if (existing) {
        return prev.map(m =>
          m.fileId === fileId
            ? {
              ...m,
              [identifierType === 'userId' ? 'userIdColumn' : 'personIdExternalColumn']: columnName,
              sampleValues: sampleValues as string[]
            }
            : m
        );
      }
      const fileMeta = filesWithColumns.find(f => f.id === fileId);
      const newMapping: EmployeeIdMapping = {
        fileId,
        fileName: fileMeta?.displayName || fileId,
        userIdColumn: identifierType === 'userId' ? columnName : undefined,
        personIdExternalColumn: identifierType === 'personIdExternal' ? columnName : undefined,
        sampleValues: sampleValues as string[]
      };
      return [...prev, newMapping];
    });
  };

  const clearMapping = (fileId: string, identifierType: 'userId' | 'personIdExternal') => {
    setMappings(prev => {
      return prev.map(m =>
        m.fileId === fileId
          ? {
            ...m,
            [identifierType === 'userId' ? 'userIdColumn' : 'personIdExternalColumn']: undefined
          }
          : m
      );
    });
  };

  const canSave = useMemo(() => {
    if (filesWithColumns.length === 0) return false;
    if (!Array.isArray(mappings)) return false;

    // Check if at least one file has at least one identifier column mapped
    const hasAnyMapping = mappings.some(mapping => mapping.userIdColumn || mapping.personIdExternalColumn);

    console.log('canSave debug:', {
      filesCount: filesWithColumns.length,
      mappingsCount: mappings.length,
      hasAnyMapping,
      mappings: mappings.map(m => ({
        fileId: m.fileId,
        fileName: m.fileName,
        userIdColumn: m.userIdColumn,
        personIdExternalColumn: m.personIdExternalColumn
      }))
    });

    return hasAnyMapping;
  }, [filesWithColumns, mappings]);

  const unmatchedSummary = useMemo(() => {
    if (!matchResult || matchResult.unmatchedEmployees.length === 0) return null;
    return matchResult.unmatchedEmployees.map(item => ({
      fileName: filesWithColumns.find(f => f.id === item.fileId)?.displayName || item.fileId,
      count: item.values.length,
      reason: item.reason
    }));
  }, [matchResult, filesWithColumns]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Employee Identifier Mapping</h1>
            <p className="text-gray-600 mt-1">
              Select the identifier type (Employment ID or Person ID) and the corresponding column from each source file.
            </p>

          </div>
        </div>

        <div className="mt-6 grid gap-4">
          {filesWithColumns.map(file => {
            const selected = mappings.find(m => m.fileId === file.id);
            return (
              <div key={file.id} className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-3">{file.displayName}</h3>
                <p className="text-xs text-gray-500 mb-4">Map identifier columns for this file. You can map both types.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* User ID Mapping */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Employment ID (userId)
                      </label>
                      {selected?.userIdColumn && (
                        <button
                          type="button"
                          onClick={() => clearMapping(file.id, 'userId')}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <select
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      value={selected?.userIdColumn || ''}
                      onChange={(e) => {
                        const columnKey = e.target.value;
                        if (columnKey) {
                          updateMapping(file.id, columnKey, 'userId');
                        } else {
                          clearMapping(file.id, 'userId');
                        }
                      }}
                    >
                      <option value="">Select column</option>
                      {file.columns.map(col => (
                        <option key={col.key} value={col.key}>{col.label}</option>
                      ))}
                    </select>
                    {selected?.userIdColumn && (
                      <p className="text-xs text-green-600">✓ Mapped to {selected.userIdColumn}</p>
                    )}
                  </div>

                  {/* Person ID External Mapping */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Person ID (personIdExternal)
                      </label>
                      {selected?.personIdExternalColumn && (
                        <button
                          type="button"
                          onClick={() => clearMapping(file.id, 'personIdExternal')}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <select
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      value={selected?.personIdExternalColumn || ''}
                      onChange={(e) => {
                        const columnKey = e.target.value;
                        if (columnKey) {
                          updateMapping(file.id, columnKey, 'personIdExternal');
                        } else {
                          clearMapping(file.id, 'personIdExternal');
                        }
                      }}
                    >
                      <option value="">Select column</option>
                      {file.columns.map(col => (
                        <option key={col.key} value={col.key}>{col.label}</option>
                      ))}
                    </select>
                    {selected?.personIdExternalColumn && (
                      <p className="text-xs text-green-600">✓ Mapped to {selected.personIdExternalColumn}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex justify-between items-center">
                  <div className="text-xs text-gray-500">
                    Status: {(selected?.userIdColumn || selected?.personIdExternalColumn) ? (
                      <span className="text-green-600 font-medium">Configured</span>
                    ) : (
                      <span className="text-orange-600 font-medium">Needs mapping</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:text-blue-800"
                    onClick={() => previewMutation.mutate(file.id)}
                    disabled={previewMutation.isPending}
                  >
                    {previewMutation.isPending ? 'Loading preview…' : 'Preview data'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 text-sm text-gray-600">
            {matchResult ? (
              <>
                <p className="text-gray-800 font-medium">
                  {matchResult.matchingEmployees.toLocaleString()} employees matched across {mappings.length} files.
                </p>
                {unmatchedSummary && unmatchedSummary.length > 0 && (
                  <p>
                    Unmatched IDs detected in {unmatchedSummary.length} file(s). You can still proceed to entity mapping.
                  </p>
                )}
              </>
            ) : (
              <p>Mapping will validate that employee IDs align across files.</p>
            )}
          </div>

          <div className="flex gap-3">
            {matchResult && (
              <button
                onClick={() => onComplete(mappings, matchResult)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 font-medium"
              >
                {unmatchedSummary && unmatchedSummary.length > 0 ? 'Continue Anyway' : 'Continue'}
              </button>
            )}
            <button
              onClick={() => {
                console.log('Validate button clicked, current mappings:', mappings);
                console.log('canSave:', canSave);
                if (canSave) {
                  // Force reset match result if re-validating to ensure onComplete isn't blocked if we were to change logic
                  saveEmployeeIdMapping.mutate(mappings);
                } else {
                  console.warn('Cannot save - validation failed');
                }
              }}
              disabled={!canSave || saveEmployeeIdMapping.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveEmployeeIdMapping.isPending ? 'Validating...' : (matchResult ? 'Re-validate' : 'Validate & Save')}
            </button>
          </div>
        </div>

        {unmatchedSummary && unmatchedSummary.length > 0 && (
          <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-4 text-sm text-red-700">
            <p className="font-medium">Unmatched Employee IDs:</p>
            <ul className="list-disc list-inside space-y-1">
              {unmatchedSummary.map(item => (
                <li key={item.fileName}>
                  <span className="font-semibold">{item.fileName}:</span> {item.count.toLocaleString()} unmatched values
                  {item.reason && <span className="block text-xs text-red-600 ml-4 mt-0.5">{item.reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Modal */}
        {previewModalOpen && activePreviewFileId && previewCache[activePreviewFileId] && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Data Preview: {filesWithColumns.find(f => f.id === activePreviewFileId)?.displayName}
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">Showing first 20 rows</p>
                </div>
                <button
                  onClick={() => setPreviewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="flex-1 overflow-auto p-4">
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  {(!previewCache[activePreviewFileId]?.rows || previewCache[activePreviewFileId]?.rows.length === 0) ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No preview data available.
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(previewCache[activePreviewFileId]?.rows?.[0] || {}).map(header => (
                            <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50 border-b">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewCache[activePreviewFileId]?.rows?.slice(0, 20).map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 group">
                            {Object.keys(previewCache[activePreviewFileId]?.rows?.[0] || {}).map(header => (
                              <td key={`${idx}-${header}`} className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap group-hover:text-blue-600">
                                {String(row[header] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="p-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
                <button
                  onClick={() => setPreviewModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeIdMappingStep;

